import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import api from '../utils/api';

// Full cost before secure payment. Every TradeSafe figure comes from the
// existing /tradesafe/checkout-quote response (TradeSafe's calculator). This
// component never applies a rate. A method without a complete quote is shown
// as unavailable, and the caller must keep Pay disabled.

const LABELS = { CARD: 'Card', OZOW: 'Ozow Instant EFT' };

export function formatRands(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `R ${Number(value).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function isRowComplete(row) {
  return Boolean(row && row.available && row.tradesafe_fee != null && row.buyer_pays != null);
}

// Returns the methods (CARD/OZOW) that may be paid with right now.
export function payableMethods(quote) {
  if (!quote || !quote.quoted) return [];
  return (quote.methods || []).filter(isRowComplete).map(row => row.method);
}

function buyerShareCents(fee, by) {
  const cents = Math.round(Number(fee || 0) * 100);
  if (by === 'seller' || by === 'agent') return 0;
  // Split integer cents half-up, as the backend's Decimal allocation does.
  if (by === 'split') return Math.floor((cents + 1) / 2);
  return cents;
}

function buyerShares(quote, row) {
  const processingPayer = row.tradesafe_fee_paid_by ?? quote.tradesafe_fee_paid_by ?? quote.trusttrade_fee_paid_by;
  const platform = row.buyer_trusttrade_fee_cents ?? buyerShareCents(quote.trusttrade_fee, row.trusttrade_fee_paid_by ?? quote.trusttrade_fee_paid_by);
  const processing = row.buyer_tradesafe_fee_cents ?? buyerShareCents(row.tradesafe_fee, processingPayer);
  const vat = row.buyer_tradesafe_vat_cents ?? buyerShareCents(row.tradesafe_vat, processingPayer);
  // VAT is part of the allocated processing total. Keep any rounding cent in
  // the net fee so the displayed lines add up to that exact buyer share.
  return { platform: platform / 100, processing: (processing - vat) / 100, vat: vat / 100 };
}

export default function FullCostQuote({ transactionId, dealId, milestoneId, onQuote }) {
  const [state, setState] = useState({ loading: true, quote: null, error: null });

  const load = useCallback(async () => {
    setState({ loading: true, quote: null, error: null });
    onQuote && onQuote(null);
    const body = {};
    if (transactionId) body.transaction_id = transactionId;
    if (dealId) body.deal_id = dealId;
    if (milestoneId) body.milestone_id = milestoneId;
    try {
      const res = await api.post('/tradesafe/checkout-quote', body, { withCredentials: true });
      const quote = res?.data || null;
      if (payableMethods(quote).length === 0) {
        setState({ loading: false, quote: null, error: 'unavailable' });
        onQuote && onQuote(null);
        return;
      }
      setState({ loading: false, quote, error: null });
      onQuote && onQuote(quote);
    } catch (err) {
      setState({ loading: false, quote: null, error: 'unavailable' });
      onQuote && onQuote(null);
    }
  }, [transactionId, dealId, milestoneId, onQuote]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) {
    return <div className="tt-full-cost" role="status" data-testid="full-cost-loading"><Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Getting TradeSafe’s fee for this payment…</div>;
  }
  if (state.error) {
    return <div className="tt-full-cost" role="alert" data-testid="full-cost-unavailable">
      <p style={{ margin: '0 0 8px' }}>We couldn’t get TradeSafe’s fee for this payment right now, so we can’t show your full total. Payment is paused until the full total is shown. No payment was started.</p>
      <button type="button" onClick={load} className="action-btn"><RefreshCw size={14} /> Retry</button>
    </div>;
  }

  const { quote } = state;
  const rows = (quote.methods || []).filter(row => row.method in LABELS)
    .map(row => ({ ...row, buyerShares: buyerShares(quote, row) }));
  const courier = Number(quote.courier_fee || 0);
  const item = quote.item_price != null ? quote.item_price : quote.item_amount;
  return <section className="tt-full-cost" aria-label="Full cost breakdown" data-testid="full-cost-quote">
    <div className="tt-money-heading"><ShieldCheck size={18} aria-hidden="true" /><h2>Full cost: what you will pay</h2></div>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
      <thead><tr><th scope="col" style={{ textAlign: 'left' }}></th>{rows.map(row => <th scope="col" key={row.method} style={{ textAlign: 'right' }}>{LABELS[row.method]}</th>)}</tr></thead>
      <tbody>
        <tr><th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>Item price</th>{rows.map(row => <td key={row.method} style={{ textAlign: 'right' }}>{formatRands(item)}</td>)}</tr>
        {courier > 0 && <tr><th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>Delivery (courier)</th>{rows.map(row => <td key={row.method} style={{ textAlign: 'right' }}>{formatRands(courier)}</td>)}</tr>}
        <tr><th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>TrustTrade fee (you pay)</th>{rows.map(row => <td key={row.method} style={{ textAlign: 'right' }}>{formatRands(row.buyerShares.platform)}</td>)}</tr>
        <tr><th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>TradeSafe processing fee (you pay)</th>{rows.map(row => <td key={row.method} style={{ textAlign: 'right' }}>{isRowComplete(row) ? formatRands(row.buyerShares.processing) : 'To be confirmed from TradeSafe'}</td>)}</tr>
        {rows.some(row => row.tradesafe_vat != null) && <tr><th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>VAT on TradeSafe fee (you pay)</th>{rows.map(row => <td key={row.method} style={{ textAlign: 'right' }}>{row.tradesafe_vat != null ? formatRands(row.buyerShares.vat) : '—'}</td>)}</tr>}
        <tr><th scope="row" style={{ textAlign: 'left' }}>Total charged to you</th>{rows.map(row => <td key={row.method} style={{ textAlign: 'right', fontWeight: 700 }} data-testid={`full-cost-total-${row.method}`}>{isRowComplete(row) ? formatRands(row.buyer_pays) : 'Not available'}</td>)}</tr>
      </tbody>
    </table>
    <p className="tt-money-explainer" style={{ fontSize: 12 }}>TradeSafe’s fee for each payment method is TradeSafe’s own quote for this payment{rows.some(row => row.tradesafe_fee_paid_by === 'split') ? ' (split between buyer and seller)' : ''}. Only your share of each fee is shown above. The amount on TradeSafe’s secure payment page should match “Total charged to you”. If it does not, don’t pay and contact us.</p>
    <button type="button" onClick={load} className="action-btn" style={{ fontSize: 12 }}><RefreshCw size={12} /> Refresh quote</button>
  </section>;
}
